import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/config/queryClient';
import { useActiveWorkspaceId } from '@/hooks/useActiveWorkspaceId';
import { supabase } from '@/integrations/supabase/client';
import {
  type CreateReachEventInput,
  type ReachEvent,
  reachEventService,
} from '@/services/data/reach/ReachEventService';

/**
 * Lists Reach events for the active workspace. `enabled` gates on both the
 * user AND a resolved workspace id — an unresolved workspace never fires the
 * `list` query, matching the pattern in `useBrandAudiences`.
 */
export function useReachEvents() {
  const workspaceQuery = useActiveWorkspaceId();

  const workspaceId = workspaceQuery.data ?? null;

  const eventsQuery = useQuery({
    queryKey: queryKeys.reach.events.list(workspaceId),
    queryFn: () => reachEventService.list(workspaceId as string),
    enabled: Boolean(workspaceId),
    staleTime: 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  return {
    events: eventsQuery.data ?? [],
    workspaceId,
    isLoading: workspaceQuery.isLoading || (Boolean(workspaceId) && eventsQuery.isLoading),
    isPending: workspaceQuery.isPending || eventsQuery.isPending,
    error: workspaceQuery.error ?? eventsQuery.error,
    refetch: eventsQuery.refetch,
  };
}

export function useCreateReachEvent() {
  const queryClient = useQueryClient();

  return useMutation<ReachEvent, Error, CreateReachEventInput>({
    mutationFn: (input) => reachEventService.create(input),
    onSuccess: (event) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.reach.events.list(event.workspaceId),
      });
    },
  });
}

export function useResearchReachEventWebsite() {
  const queryClient = useQueryClient();

  return useMutation<{ event: unknown }, Error, { eventId: string; workspaceId: string }>({
    mutationFn: async ({ eventId, workspaceId }) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const response = await fetch('/api/reach/research-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ eventId, workspaceId }),
      });
      const payload = (await response.json()) as { event?: unknown; error?: string };
      if (!response.ok || !payload.event) {
        throw new Error(payload.error || 'Could not research the event website');
      }
      return { event: payload.event };
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.reach.events.list(variables.workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.reach.events.detail(variables.workspaceId, variables.eventId),
      });
    },
  });
}
